import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";

import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton
} from "@mui/material";
import { Close } from "@mui/icons-material";

import { RootState } from "../../../store/Index";
import { IEditRateDialogProps, IRateCardProps, IRoleSkillTool } from "../types/rate-card";
import apiService from "../../../service/Api";
import CustomTextField from "../../../components/common/Textfield";

const rateTypes = ['hourly', 'stream', 'daily', 'salaried'];

export const EditRateDialog: React.FC<IEditRateDialogProps> = ({
  formData,
  formLoading,
  handleEditRate,
  handleInputChange,
  handleRolesEdit,
  open,
  rateType,
  resetForm,
  selectedRate,
  selectedRoles, // this version of selectedRoles is not correct, this component should get selectedRoles from the one defined in the database for that rate card 
  setDialogOpen,
  setFormLoading,
  setFormData,
  setSelectedRoles,
  setRateType,
}) => {
  const [roleOptions, setRoleOptions] = useState<string[]>([]);
  const [roleVal, setRoleVal] = useState<string | null>("");
  const [rolesLoading, setRolesLoading] = useState(false);

  const [rateTypeVal, setRateTypeVal] = useState<string | null>("");

  const reduxTheme = useSelector((state: RootState) => state.theme);

  const fetchRateCard = async () => {
    setFormLoading(true);
    try {
      const rate = await apiService.get<IRateCardProps>(
        `/api/crews/role-ratings/${selectedRate}/`
      );
      setFormData({
        role: {
          name: rate.data.role.name,
          description: rate.data.role.description
        },
        roleId: rate.data.role.id,
        rate: rate.data.rate,
        currency: 'USD',
        effective_date: rate.data.effective_date,
        type: rate.data.rating_type,
        last_updated_by: rate.data.last_updated_by
      });
      setSelectedRoles(`${rate.data.role.id} - ${rate.data.role.name}`);
      setRateType(rate.data.rating_type);
      setRateTypeVal(rate.data.rating_type);
    } catch (err) {
      console.error("Failed to fetch rate card:", err);
      resetForm();
    } finally {
      setFormLoading(false);
    }
  };

  useEffect(() => {
    if (selectedRate) {
      fetchRateCard();
    }
  }, [selectedRate]);

  const fetchRoles = async () => {
    setRolesLoading(true);
    try {
      const roles = await apiService.get<{ results: IRoleSkillTool[] }>(
        "/api/crews/roles/",
        { page_size: 20 }
      );

      const mapped = roles.data.results.map(rl => (
        `${rl.id} - ${rl.name}`
      ));

      setRoleOptions(mapped);
    } catch (err) {
      console.error("Failed to fetch roles:", err);
      setRoleOptions([]);
    } finally {
      setRolesLoading(false);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, [open]);

  if (!formData) return null;

  return (
    <Dialog
      open={open}
      onClose={() => {
        setDialogOpen(false);
        resetForm();
      }}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>
        Editing {formData.role.name} Rate Card
        <IconButton
          onClick={() => {
            setDialogOpen(false);
            setSelectedRoles(undefined);
            setRateType("");
            resetForm();
          }}
          sx={{ position: "absolute", right: 8, top: 8 }}
          disabled={formLoading}
        >
          <Close />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Grid container spacing={2}>
          <Grid size={12}>
            <Autocomplete
              value={roleVal}
              onChange={(event: React.SyntheticEvent, newValue: string | null) => {
                setRoleVal(newValue);
              }}
              inputValue={selectedRoles}
              onInputChange={(event, newInputValue) => {
                handleRolesEdit(newInputValue);
              }}
              options={roleOptions}
              sx={{ width: '100%', borderRadius: 2 }}
              renderInput={(params) => 
                <CustomTextField
                  {...params}
                  placeholder="Select role..."
                  variant="outlined"
                  label={'Roles'}
                  helperText="Type 3 letters to search"
                />
              }
            />
          </Grid>
          <Grid size={6}>
            <CustomTextField
              name="rate"
              label="Rate Amount"
              placeholder="Enter rate amount"
              fullWidth
              value={formData.rate}
              onChange={(e) => handleInputChange("rate", e.target.value)}
              disabled={formLoading}
            />
          </Grid>
          <Grid size={6}>
            <Autocomplete
              value={rateTypeVal}
              onChange={(event: React.SyntheticEvent, newValue: string | null) => {
                setRateTypeVal(newValue);
              }}
              inputValue={rateType}
              onInputChange={(event, newInputValue) => {
                setRateType(newInputValue);
              }}
              options={rateTypes}
              sx={{ width: '100%', borderRadius: 2 }}
              renderInput={(params) => 
                <CustomTextField
                  {...params}
                  placeholder="Select rate type..."
                  variant="outlined"
                  label={'Rate Types'}
                  helperText="Type 3 letters to search"
                />
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomTextField
              name="currency"
              label="Currency"
              fullWidth
              type={'text'}
              variant="outlined"
              value={formData.currency}
              disabled
              onChange={(e) => handleInputChange("currency", e.target.value)}
            />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomTextField
              name="effective_date"
              label="Effective Date"
              slotProps={{
                inputLabel: {
                  shrink: true
                }
              }}
              fullWidth                  
              type="date"
              variant="outlined"
              value={formData.effective_date}
              onChange={(e) => handleInputChange("effective_date", e.target.value)}
            />
          </Grid>  
        </Grid>
      </DialogContent>
      <DialogActions>
        <Button
          variant="contained"
          onClick={() => {
            setDialogOpen(false);
            setSelectedRoles(undefined);
            setRateType("");
            resetForm();
          }}
          sx={{
            color: reduxTheme.primary_colors[400],
            bgcolor: 'transparent',
            border: '1px solid',
            borderColor: reduxTheme.primary_colors[400]
          }}
        >
          Discard
        </Button>
        <Button
          variant="contained"
          onClick={handleEditRate}
          disabled={formLoading}
          sx={{ bgcolor: reduxTheme.primary_colors[400] }}
        >
          {formLoading && <CircularProgress size={20} sx={{ mr: 1 }} />}Update Rate Card
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default EditRateDialog;
